import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import {
  breakerSelectivityRunCreateRequestSchema,
  type BreakerSelectivityRunCreateRequest,
} from "../services/breakerSelectivity";
import {
  breakerKindSchema,
  dutyBasisSchema,
  selectivityKindSchema,
} from "../services/breakerSelectivityContract";
import type { FaultRunFaultLevel } from "../services/faultLink";
import type { SldElement, SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import {
  BREAKER_KIND_LABELS,
  BREAKER_SELECTIVITY_DRAFT_MODULE,
  BREAKER_SELECTIVITY_LABELS,
  DUTY_BASIS_LABELS,
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  SELECTIVITY_KIND_LABELS,
  SELECTIVITY_REQUIRED_CHOICES,
  SELECTIVITY_REQUIRED_LABELS,
  breakerSelectivityDraftSchema,
  buildBreakerSelectivityRunPayload,
  createBreakerDeviceDraft,
  createInitialBreakerSelectivityDraft,
  createOutgoingDeviceDraft,
  seedOutgoingDeviceIdCounter,
  type BreakerDeviceDraft,
  type BreakerSelectivityDraft,
  type FaultLevelSource,
  type OutgoingDeviceDraft,
} from "./breakerSelectivityDraft";
import {
  FaultRunSelector,
  linkedDetailText as sharedLinkedDetailText,
  linkedDetailTitle as sharedLinkedDetailTitle,
} from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker } from "./SldElementPicker";
import { TYPED_HINT } from "./StarterCoordinationForm";

/** The first option of every choice: nothing is chosen for the engineer (A21 (b)). */
const CHOOSE = "Choose…";

export { TYPED_HINT };

// The words of the fault-run selector live with the shared selector (A27 (e)); re-exported
// here for the pages and tests that read them from this form.
export {
  FAULT_RUN_NOT_LINKABLE,
  NO_FAULT_RUNS_NOTE,
  NO_OPEN_REVISION_NOTE,
  NOT_MAXIMUM_THREE_PHASE_NOTE,
  busMismatchNote,
  faultRunOptionText,
} from "./FaultRunSelector";

export const NO_PEAK_NOTE =
  "This fault run has no peak current; the making check will be Engineering review required.";

export const ICS_HINT = "Required when the duty basis is Service (Ics).";

export const SELECTIVITY_HINT =
  "From the manufacturer's selectivity table for this exact incomer and outgoing pair. The " +
  "engine checks the declared evidence; it does not prove selectivity.";

export const CASCADING_HINT =
  "Only if the manufacturer's cascading table covers this exact pair. A result that relies " +
  "on cascading is always flagged for Engineering review.";

export const PROFILE_NOTE =
  "The breaker duty and selectivity checks carry no profile-dependent value; the profile is " +
  "recorded with the run and must match the design basis of the project revision.";

/** The read-only line under the selector as this study shows it: Ik'' and ip. */
export function linkedDetailText(level: FaultRunFaultLevel): string {
  return sharedLinkedDetailText(level, true);
}

/** The tooltip of that line: the exact engine decimals. */
export function linkedDetailTitle(level: FaultRunFaultLevel): string {
  return sharedLinkedDetailTitle(level, true);
}

/** The note when the picked bus's main supply carries no device to prefill the incomer from. */
export function noMainSupplyDeviceNote(busCode: string): string {
  return `The main supply of ${busCode} has no device in the diagram.`;
}

/**
 * What the diagram says a breaker study at a bus starts from (A27 (f)): the device of the
 * bus's main supply (the first of the bus's incomers, primary feed first, when it is a device
 * on that feed: a source incomer's, a transformer's secondary incomer or, for a feeder-fed
 * bus, the feeder's, A21) and every device on a feeder out of the bus, by code. `missing` is
 * true when the main supply is a transformer or a source incomer without a device. Null
 * without a diagram or when the bus is not in it.
 */
export function sldBusPrefill(
  elements: SldElements | null | undefined,
  busCode: string,
): {
  primaryFeed: string | null;
  incomer: SldElement | null;
  missing: boolean;
  outgoing: SldElement[];
} | null {
  if (!elements || elements.version < 1) {
    return null;
  }
  const byCode = new Map(elements.elements.map((element) => [element.code, element]));
  const bus = byCode.get(busCode);
  const summary = elements.buses.find((candidate) => candidate.code === busCode);
  if (bus === undefined || bus.category !== "bus" || summary === undefined) {
    return null;
  }
  const primaryFeed = bus.upstream_paths?.[0]?.[0] ?? null;
  const first = summary.incomers[0] === undefined ? undefined : byCode.get(summary.incomers[0]);
  const incomer =
    first !== undefined &&
    first.category === "device" &&
    primaryFeed !== null &&
    first.upstream_paths?.[0]?.[0] === primaryFeed
      ? first
      : null;
  const outgoing = summary.outgoing_devices
    .map((code) => byCode.get(code))
    .filter((element): element is SldElement => element !== undefined);
  const feed = primaryFeed === null ? undefined : byCode.get(primaryFeed);
  const missing =
    incomer === null &&
    feed !== undefined &&
    (feed.category === "transformer" || feed.category === "incomer");
  return { primaryFeed, incomer, missing, outgoing };
}

type BreakerSelectivityFormProps = {
  disabled?: boolean;
  onSubmit: (payload: BreakerSelectivityRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the bus and the
  // devices are picked from it, and a picked bus prefills its incomer and outgoing devices.
  sldElements?: SldElements | null | undefined;
};

// The LV breaker duty and selectivity study (EOS-05b, A21). The form owns the
// draft. The fault level starts on a linked fault run of the active open revision
// (A21 (a)); the runs are listed from the fault runs API and the chosen run's
// stored Ik'' and ip are shown read-only, for the engineer's eyes only - the
// backend reads the same stored result again when the study is saved. A typed
// value is allowed and said to be review required. Every choice starts unchosen:
// the backend imposes no default (A21 (b), (c)).
//
// The draft is kept under its own module key, "breaker-selectivity"; no form
// ever reads, restores or deletes another's draft.
//
// The payload carries no project revision: the page adds the selected revision
// when it calls the API, exactly as the other study pages do.
export function BreakerSelectivityForm({
  disabled = false,
  onSubmit,
  sldElements,
}: BreakerSelectivityFormProps) {
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<BreakerSelectivityDraft>({
      module: BREAKER_SELECTIVITY_DRAFT_MODULE,
      createInitial: createInitialBreakerSelectivityDraft,
      schema: breakerSelectivityDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowToFocus, setRowToFocus] = useState<string | null>(null);
  // Said once, under the incomer, when the picked bus's main supply has no device.
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const linkedSource = draft.faultLevelSource === "LINKED";

  // A new row sits among several fieldsets, so the cursor goes to it.
  useEffect(() => {
    if (rowToFocus === null) {
      return;
    }
    formRef.current?.querySelector<HTMLElement>(`[name="${rowToFocus}-code"]`)?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  function updateStudy(patch: Partial<BreakerSelectivityDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  // Stable for the selector's effects: it clears a run the listed revision does not hold.
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  function updateIncomer(patch: Partial<BreakerDeviceDraft>) {
    if (patch.code !== undefined) {
      setPrefillNote(null);
    }
    setDraft((current) => ({ ...current, incomer: { ...current.incomer, ...patch } }));
  }

  // A device nobody has typed in yet: the prefill may fill it, never a typed one.
  const isBlankDevice = (device: BreakerDeviceDraft): boolean =>
    Object.values(device).every((value) => value === "");
  const isBlankOutgoing = (row: OutgoingDeviceDraft): boolean =>
    Object.entries(row).every(([field, value]) => field === "id" || value === "");
  const breakerKind = (kind: string | null): string =>
    kind !== null && (breakerKindSchema.options as readonly string[]).includes(kind) ? kind : "";

  // The bus picked from the diagram: while the incomer and every outgoing row are blank, the
  // incomer becomes the device of the bus's main supply and the outgoing rows the devices on
  // its feeders, code, kind and In filled (A27 (f)); Icu, Ics, Icm and the duty basis are
  // catalogue data the engineer types. A main supply without a device leaves the incomer
  // blank and says so.
  function chooseBus(busCode: string) {
    setPrefillNote(null);
    const prefill = sldBusPrefill(sldElements, busCode);
    const untouched = isBlankDevice(draft.incomer) && draft.outgoing.every(isBlankOutgoing);
    if (prefill === null || !untouched) {
      updateStudy({ busCode });
      return;
    }
    const fromSld = (element: SldElement): BreakerDeviceDraft => ({
      ...createBreakerDeviceDraft(),
      code: element.code,
      kind: breakerKind(element.kind),
      ratedCurrentA: element.rating ?? "",
    });
    if (prefill.missing) {
      setPrefillNote(noMainSupplyDeviceNote(busCode));
    }
    seedOutgoingDeviceIdCounter(draft.outgoing.map((row) => row.id));
    const outgoing =
      prefill.outgoing.length === 0
        ? draft.outgoing
        : prefill.outgoing.map((element) => ({
            ...createOutgoingDeviceDraft(),
            ...fromSld(element),
          }));
    setDraft((current) => ({
      ...current,
      busCode,
      incomer: prefill.incomer === null ? current.incomer : fromSld(prefill.incomer),
      outgoing,
    }));
  }

  function updateOutgoing(id: string, patch: Partial<OutgoingDeviceDraft>) {
    setDraft((current) => ({
      ...current,
      outgoing: current.outgoing.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  }

  function addOutgoing() {
    // A restored draft brings ids the counter of this page load has not handed out yet.
    seedOutgoingDeviceIdCounter(draft.outgoing.map((row) => row.id));
    const row = createOutgoingDeviceDraft();
    setDraft((current) => ({ ...current, outgoing: [...current.outgoing, row] }));
    setRowToFocus(row.id);
  }

  function removeOutgoing(id: string) {
    setDraft((current) => ({
      ...current,
      outgoing: current.outgoing.filter((row) => row.id !== id),
    }));
  }

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "fault_run_id") {
      return issue.message;
    }
    return describeValidationIssue(issue, BREAKER_SELECTIVITY_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = breakerSelectivityRunCreateRequestSchema.safeParse(
      buildBreakerSelectivityRunPayload(draft),
    );

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the breaker selectivity inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (label: string, field: keyof BreakerSelectivityDraft, name: string) => (
    <label>
      {label}
      <input
        name={name}
        value={draft[field] as string}
        onChange={(event) =>
          updateStudy({ [field]: event.target.value } as Partial<BreakerSelectivityDraft>)
        }
      />
    </label>
  );

  // One device's inputs, the incomer's or an outgoing row's. Field names carry the
  // device's prefix ("incomer-code", "breaker-3-code"), stable across add and remove.
  type DeviceUpdate = (patch: Partial<BreakerDeviceDraft>) => void;

  const deviceText = (
    prefix: string,
    device: BreakerDeviceDraft,
    update: DeviceUpdate,
    label: string,
    field: keyof BreakerDeviceDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${prefix}-${field}`}
        value={device[field]}
        onChange={(event) => update({ [field]: event.target.value })}
      />
    </label>
  );

  // A select with "Choose…" first and the contract's values after it.
  const choice = <T extends string>(
    name: string,
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select name={name} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  const deviceFields = (prefix: string, device: BreakerDeviceDraft, update: DeviceUpdate) => (
    <>
      <SldElementPicker
        label="Device code"
        name={`${prefix}-code`}
        value={device.code}
        onChange={(code) => update({ code })}
        categories={["device"]}
        elements={sldElements}
      />
      {choice(
        `${prefix}-kind`,
        "Breaker type",
        device.kind,
        (kind) => update({ kind }),
        breakerKindSchema.options,
        BREAKER_KIND_LABELS,
      )}
      {deviceText(prefix, device, update, "Rated current In (A)", "ratedCurrentA")}
      {deviceText(
        prefix,
        device,
        update,
        "Rated operational voltage Ue (V)",
        "ratedOperationalVoltageV",
      )}
      {deviceText(
        prefix,
        device,
        update,
        "Ultimate breaking capacity Icu (kA)",
        "ultimateBreakingCapacityKa",
      )}
      {choice(
        `${prefix}-dutyBasis`,
        "Duty basis",
        device.dutyBasis,
        (dutyBasis) => update({ dutyBasis }),
        dutyBasisSchema.options,
        DUTY_BASIS_LABELS,
      )}
      {deviceText(
        prefix,
        device,
        update,
        "Service breaking capacity Ics (kA)",
        "serviceBreakingCapacityKa",
      )}
      <p data-ics-hint="true">{ICS_HINT}</p>
      {deviceText(prefix, device, update, "Rated making capacity Icm (kA peak)", "makingCapacityKa")}
    </>
  );

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Breaker selectivity inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code", "studyCode")}
        {studyText("Study name", "name", "studyName")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={chooseBus}
          categories={["bus"]}
          elements={sldElements}
        />
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">
              Australia / New Zealand (AS/NZS 3000) - reference data pending
            </option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes", "studyNotes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Fault level</legend>
        <label>
          Fault level source
          <select
            name="faultLevelSource"
            value={draft.faultLevelSource}
            onChange={(event) =>
              updateStudy({ faultLevelSource: event.target.value as FaultLevelSource })
            }
          >
            {FAULT_LEVEL_SOURCES.map((source) => (
              <option key={source} value={source}>
                {FAULT_LEVEL_SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>

        {linkedSource ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak
            noPeakNote={NO_PEAK_NOTE}
          />
        ) : (
          <>
            {studyText(
              "Prospective current Ik'' (kA)",
              "typedProspectiveCurrentKa",
              "typedProspectiveCurrentKa",
            )}
            {studyText("Peak current ip (kA)", "typedPeakCurrentKa", "typedPeakCurrentKa")}
            {studyText(
              "System nominal voltage (V)",
              "typedSystemNominalVoltageV",
              "typedSystemNominalVoltageV",
            )}
            {studyText("Fault bus code", "typedFaultBusCode", "typedFaultBusCode")}
            <p data-typed-hint="true">{TYPED_HINT}</p>
          </>
        )}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Incomer</legend>
        {prefillNote ? <p data-sld-prefill-note="true">{prefillNote}</p> : null}
        {deviceFields("incomer", draft.incomer, updateIncomer)}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Outgoing</legend>
        {draft.outgoing.map((row, index) => {
          const number = index + 1;
          const update = (patch: Partial<OutgoingDeviceDraft>) => updateOutgoing(row.id, patch);
          return (
            <fieldset key={row.id} data-outgoing-row={row.id}>
              <legend>Outgoing {number}</legend>

              <fieldset>
                <legend>Device</legend>
                {deviceFields(row.id, row, update)}
              </fieldset>

              <fieldset>
                <legend>Selectivity with the incomer</legend>
                {choice(
                  `${row.id}-selectivityRequired`,
                  "Selectivity with the incomer",
                  row.selectivityRequired,
                  (selectivityRequired) => update({ selectivityRequired }),
                  SELECTIVITY_REQUIRED_CHOICES,
                  SELECTIVITY_REQUIRED_LABELS,
                )}
                {choice(
                  `${row.id}-declaredSelectivityKind`,
                  "Declared selectivity",
                  row.declaredSelectivityKind,
                  (declaredSelectivityKind) => update({ declaredSelectivityKind }),
                  selectivityKindSchema.options,
                  SELECTIVITY_KIND_LABELS,
                )}
                {row.declaredSelectivityKind === "PARTIAL" ? (
                  <label>
                    Selectivity limit (kA)
                    <input
                      inputMode="decimal"
                      name={`${row.id}-selectivityLimitKa`}
                      value={row.selectivityLimitKa}
                      onChange={(event) => update({ selectivityLimitKa: event.target.value })}
                    />
                  </label>
                ) : null}
                <p data-selectivity-hint="true">{SELECTIVITY_HINT}</p>
              </fieldset>

              <fieldset>
                <legend>Cascading</legend>
                <label>
                  Cascading limit with the incomer (kA)
                  <input
                    inputMode="decimal"
                    name={`${row.id}-cascadingLimitKa`}
                    value={row.cascadingLimitKa}
                    onChange={(event) => update({ cascadingLimitKa: event.target.value })}
                  />
                </label>
                <p data-cascading-hint="true">{CASCADING_HINT}</p>
              </fieldset>

              {draft.outgoing.length > 1 ? (
                <button type="button" onClick={() => removeOutgoing(row.id)}>
                  Remove outgoing {number}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        <button type="button" onClick={addOutgoing}>
          Add outgoing
        </button>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check breaker duty and selectivity
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default BreakerSelectivityForm;
